/**
 * MarginControl — Widget prominent d'application de marge.
 *
 * Permet à l'utilisateur d'appliquer instantanément une marge en % sur
 * TOUTES les lignes en un clic. Slider natif + input + boutons rapides.
 *
 * Props :
 *   - onApply(pct: number) : callback qui recalcule tous les unit_price_ht
 *   - currentMargin: string|null : marge courante affichée en indicateur
 *   - initialPct: number|null : marge implicite détectée du template
 *     (auto-remplit le slider et ajoute un badge "détectée du template")
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Percent, Zap, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

const QUICK = [10, 15, 20, 25, 30, 40];

export default function MarginControl({ onApply, currentMargin, initialPct }) {
  const [pct, setPct] = useState(() =>
    Number.isFinite(initialPct) && initialPct !== null ? initialPct : 20
  );
  // Re-sync le slider si initialPct change (ex : le user choisit un template
  // en Étape 2 puis arrive en Étape 3, on veut afficher la marge détectée).
  useEffect(() => {
    if (Number.isFinite(initialPct) && initialPct !== null) {
      setPct(initialPct);
    }
  }, [initialPct]);

  const apply = (v) => {
    if (Number.isFinite(v) && v > -100 && v < 500) onApply(v);
  };

  const hasDetection = Number.isFinite(initialPct) && initialPct !== null;

  return (
    <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-xl p-4"
          data-testid="rebilling-margin-control">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-7 w-7 rounded-md bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
          <Percent className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            Marge à appliquer
            {hasDetection && (
              <span className="inline-flex items-center gap-1 text-[10px] font-normal px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200"
                    data-testid="rebilling-margin-detected-badge">
                <Sparkles className="h-2.5 w-2.5" />
                Détectée du template : {initialPct}%
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500">
            Applique instantanément un % sur tous les prix unitaires
            {currentMargin !== null && currentMargin !== undefined && (
              <span className="ml-1 font-mono text-violet-700"> — actuelle : {currentMargin}%</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <input
          id="rebilling-margin-slider"
          name="rebilling-margin-slider"
          type="range"
          min="-20" max="100" step="1"
          value={pct}
          onChange={(e) => setPct(Number(e.target.value))}
          className="flex-1 accent-violet-600"
          data-testid="rebilling-margin-slider"
        />
        <div className="flex items-center gap-1">
          <Input
            type="number" step="0.5" min="-100" max="500"
            value={pct}
            onChange={(e) => setPct(Number(e.target.value))}
            className="h-9 w-20 text-right font-mono bg-white border-violet-200"
            data-testid="rebilling-margin-input"
          />
          <span className="text-sm text-slate-500 font-medium">%</span>
        </div>
        <Button
          onClick={() => apply(pct)}
          className="bg-violet-600 hover:bg-violet-700 text-white gap-1"
          data-testid="rebilling-margin-apply"
        >
          <Zap className="h-3.5 w-3.5" /> Appliquer
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <span className="text-[11px] text-slate-500 self-center mr-1">Rapide :</span>
        {QUICK.map((v) => (
          <button
            key={v}
            onClick={() => { setPct(v); apply(v); }}
            className={cn(
              "px-2.5 py-1 text-xs rounded-md border font-medium",
              pct === v
                ? "border-violet-400 bg-violet-100 text-violet-800"
                : "border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50 text-slate-700"
            )}
            data-testid={`rebilling-margin-quick-${v}`}
          >
            {v}%
          </button>
        ))}
      </div>
    </Card>
  );
}
